import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Send, Loader, ExternalLink, AlertCircle } from 'lucide-react';
import { useQuery } from 'react-query';
import api from '../api/client';
import endpoints from '../api/endpoints';
import ReactMarkdown from 'react-markdown';
import '../assets/css/query.css';
import LoadingSpinner from '../components/LoadingSpinner';
import FeedbackModal from '../components/FeedbackModal';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

// Styled-components removed in favor of centralized CSS in assets/css/query.css

const QueryInterface = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [loadingThread, setLoadingThread] = useState(false);
  const [filters, setFilters] = useState({
    contentType: '',
    sourceType: ''
  });
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const chatBodyRef = useRef(null);

  const { data: suggestions } = useQuery(
    'suggestions',
    () => api.get(endpoints.queries.suggestions).then(res => res.data),
    { staleTime: 5 * 60 * 1000 }
  );

  const { data: filtersData } = useQuery(
    'filters',
    () => api.get(endpoints.queries.filters).then(res => res.data),
    { staleTime: 5 * 60 * 1000 }
  );

  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState(() => {
    // Load from localStorage on mount
    const saved = localStorage.getItem('nickChatHistory');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Restore Date objects
        return parsed.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      } catch (e) {
        console.error('Failed to parse chat history:', e);
        return [];
      }
    }
    return [];
  });
  const [currentThread, setCurrentThread] = useState(() => {
    return localStorage.getItem('nickCurrentThread') || null;
  });
  const [isNewThread, setIsNewThread] = useState(() => {
    const saved = localStorage.getItem('nickIsNewThread');
    return saved ? JSON.parse(saved) : true;
  });

  // Parse markdown messages back into chat history
  const parseMarkdownToHistory = (markdownText) => {
    const messages = [];
    const messageRegex = /## \[(.*?)\] (User|Nick)\n([\s\S]*?)(?=\n## \[|$)/g;
    let match;
    
    while ((match = messageRegex.exec(markdownText)) !== null) {
      const [, timestamp, role, content] = match;
      messages.push({
        id: Date.now() + messages.length,
        type: role === 'User' ? 'user' : 'assistant',
        content: content.trim(),
        timestamp: new Date(timestamp),
        sources: []
      });
    }
    
    return messages;
  };

  // Load conversation thread if continuing from history
  useEffect(() => {
    const continueThreadId = location.state?.continueThreadId;
    
    if (continueThreadId) {
      setLoadingThread(true);
      
      api.get(endpoints.conversations.get(continueThreadId))
        .then(response => {
          const thread = response.data.thread;
          
          // Parse markdown messages into chat history
          const history = parseMarkdownToHistory(thread.messages);
          setChatHistory(history);
          
          // Set thread ID and mark as existing thread
          setCurrentThread(thread._id);
          setIsNewThread(false);
          
          // Update localStorage
          localStorage.setItem('nickChatHistory', JSON.stringify(history));
          localStorage.setItem('nickCurrentThread', thread._id);
          localStorage.setItem('nickIsNewThread', 'false');
          
          toast.success('Conversation loaded');
        })
        .catch(error => {
          console.error('Error loading conversation:', error);
          toast.error('Failed to load conversation');
        })
        .finally(() => {
          setLoadingThread(false);
          // Clear the navigation state so refresh doesn't reload
          window.history.replaceState({}, document.title);
        });
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    if (loadingThread) return; // Don't allow submit while loading thread
    e.preventDefault();
    if (!query.trim()) return;

    const currentQuery = query;
    setQuery(''); // Clear input immediately
    setLoading(true);
    
    // Add user message to chat history
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: currentQuery,
      timestamp: new Date()
    };
    
    setChatHistory(prev => [...prev, userMessage]);
    
    try {
      const result = await api.post(endpoints.queries.ask, {
        question: currentQuery,
        filters: Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value)
        ),
        threadId: currentThread,
        isNewThread: isNewThread
      });
      
      // Add Nick's response to chat history
      const nickMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: result.data.answer || result.data.response,
        timestamp: new Date(),
        sources: result.data.sources || [],
        query: currentQuery, // Store the query for feedback purposes
        threadId: currentThread
      };
      
      setChatHistory(prev => [...prev, nickMessage]);
      setResponse(result.data);
      
      // If this was a new thread, set the thread ID
      if (isNewThread && result.data.threadId) {
        setCurrentThread(result.data.threadId);
        setIsNewThread(false);
      }
      
    } catch (error) {
      console.error('Query error:', error);
      
      // Add error message to chat history
      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: 'Sorry, I encountered an error processing your question. Please try again.',
        timestamp: new Date()
      };
      
      setChatHistory(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Save chat state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('nickChatHistory', JSON.stringify(chatHistory));
  }, [chatHistory]);

  useEffect(() => {
    if (currentThread) {
      localStorage.setItem('nickCurrentThread', currentThread);
    } else {
      localStorage.removeItem('nickCurrentThread');
    }
  }, [currentThread]);

  useEffect(() => {
    localStorage.setItem('nickIsNewThread', JSON.stringify(isNewThread));
  }, [isNewThread]);

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
  };

  const handleSuggestionSelect = (e) => {
    const suggestion = e.target.value;
    if (suggestion) {
      setQuery(suggestion);
      e.target.value = ''; // Reset dropdown
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const startNewThread = () => {
    setCurrentThread(null);
    setIsNewThread(true);
    setChatHistory([]);
    setResponse(null);
    setQuery('');
    // Clear localStorage
    localStorage.removeItem('nickChatHistory');
    localStorage.removeItem('nickCurrentThread');
    localStorage.setItem('nickIsNewThread', 'true');
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [chatHistory, loading]);

  return (
    <div className="query-container">
      {chatHistory.length === 0 && (
        <div className="query-header">
          <img className="nick-avatar" src="/Nick-Logo.png" alt="Nick" />
          <h1 className="query-title">Ask Nick</h1>
          <p className="query-subtitle">Get scientific insights about nano bubbles and biologic systems</p>
        </div>
      )}

      {chatHistory.length > 0 ? (
        <div className="chat-unified-container">
          <div className="chat-unified-header">
            <div className="chat-unified-header-left">
              <img className="chat-header-logo" src="/Nick-Logo.png" alt="Nick" />
              <h3 className="chat-unified-title">Conversation with Nick</h3>
            </div>
            <button className="new-thread-button" onClick={startNewThread}>New Thread</button>
          </div>
          
          <div className="chat-unified-body" ref={chatBodyRef}>
            {chatHistory.map((message) => (
              <div key={message.id} className={`message ${message.type === 'user' ? 'message--user' : ''}`}>
                <div className={`message-bubble ${message.type === 'user' ? 'message-bubble--user' : ''}`}>
                  <div className="message-header">
                    {message.type !== 'user' && (
                      <img className="message-avatar" src="/Nick-Logo.png" alt="Nick" />
                    )}
                    <span className="message-sender">{message.type === 'user' ? 'You' : 'Nick'}</span>
                    <span className="message-time">{message.timestamp.toLocaleTimeString()}</span>
                  </div>
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                  {user?.role === 'admin' && message.sources && message.sources.length > 0 && (
                    <div className="sources-list">
                      <strong>Sources:</strong>
                      <ul>
                        {message.sources.map((source, index) => (
                          <li key={index}>
                            {source.title || source.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {user?.role === 'admin' && message.type === 'assistant' && (
                    <button 
                      className="report-issue-button"
                      onClick={() => setFeedbackMessage(message)}
                      title="Report an issue with this response"
                    >
                      <AlertCircle size={16} />
                      Report Issue
                    </button>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="message">
                <div className="message-bubble">
                  <LoadingSpinner text="Nick is thinking..." />
                </div>
              </div>
            )}
          </div>

          <div className="chat-unified-footer">
            <form className="chat-unified-form" onSubmit={handleSubmit}>
              <div className="chat-unified-input-group">
                <textarea
                  className="chat-unified-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your message..."
                  required
                  rows={2}
                />
                <button className="chat-unified-send-button" type="submit" disabled={loading || !query.trim()}>
                  {loading ? <Loader size={20} className="animate-spin" /> : <Send size={20} />}
                </button>
              </div>
              
              <div className="chat-unified-filters">
                <select
                  className="filter-select"
                  onChange={handleSuggestionSelect}
                  defaultValue=""
                >
                  <option value="">Suggested Questions</option>
                  {suggestions?.suggestions?.map((suggestion, index) => (
                    <option key={index} value={suggestion}>{suggestion}</option>
                  ))}
                </select>

                <select
                  className="filter-select"
                  value={filters.contentType}
                  onChange={(e) => setFilters({ ...filters, contentType: e.target.value })}
                >
                  <option value="">All Content Types</option>
                  {filtersData?.filters?.contentType?.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>

                <select
                  className="filter-select"
                  value={filters.sourceType}
                  onChange={(e) => setFilters({ ...filters, sourceType: e.target.value })}
                >
                  <option value="">All Source Types</option>
                  {filtersData?.filters?.sourceType?.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <form className="query-form" onSubmit={handleSubmit}>
        <div className="input-group">
          <textarea
            className="query-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Nick about nano bubble technology, biological systems, or any scientific question..."
            required
          />
          <button className="submit-button" type="submit" disabled={loading || !query.trim()}>
            {loading ? <Loader size={20} className="animate-spin" /> : <Send size={20} />}
            {loading ? 'Thinking...' : 'Ask'}
          </button>
        </div>

        <div className="filters-container">
          <select
            className="filter-select"
            value={filters.contentType}
            onChange={(e) => setFilters({ ...filters, contentType: e.target.value })}
          >
            <option value="">All Content Types</option>
            {filtersData?.filters?.contentType?.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <select
            className="filter-select"
            value={filters.sourceType}
            onChange={(e) => setFilters({ ...filters, sourceType: e.target.value })}
          >
            <option value="">All Source Types</option>
            {filtersData?.filters?.sourceType?.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        </form>
      )}

      {suggestions && chatHistory.length === 0 && (
        <div className="suggestions-container">
          <h3 className="suggestions-title">Suggested Questions</h3>
          <div className="suggestions-grid">
            {suggestions.suggestions.map((suggestion, index) => (
              <button
                className="suggestion-card"
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <p className="suggestion-text">{suggestion}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {feedbackMessage && (
        <FeedbackModal
          message={feedbackMessage}
          onClose={() => setFeedbackMessage(null)}
        />
      )}
    </div>
  );
};

export default QueryInterface;
